import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import useScreenType from '../../hooks/useScreenType';
import { DesktopSponsorship } from './DesktopSponsorship';
import { MobileSponsorship } from './MobileSponsorship';
import { SponsorshipStats } from './types';
import ErrorBoundary from '../../components/ErrorBoundary';
import './SponsorshipPage.css';

const SponsorshipPage: React.FC = () => {
  const { token } = useAuth();
  const { isMobile } = useScreenType();
  const [stats, setStats] = useState<SponsorshipStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    console.log('[SponsorshipPage] Initialization: isMobile =', isMobile, 'token =', token ? 'PRESENT' : 'MISSING');
    
    const fetchStats = async () => {
      try {
        console.log('[SponsorshipPage] Fetching stats from:', `${API_URL}/v1/referral/me`);
        const response = await fetch(`${API_URL}/v1/referral/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log('[SponsorshipPage] Response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('[SponsorshipPage] Stats fetched successfully:', data);
          setStats(data);
        } else {
          console.error('[SponsorshipPage] Response not OK');
          setError(true);
        }
      } catch (error) {
        console.error('[SponsorshipPage] Error fetching referral stats:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchStats();
    } else {
      console.warn('[SponsorshipPage] Token is missing, cannot fetch stats');
      // If we are definitely done loading auth and still have no token, show error
      setLoading(false);
      setError(true);
    }
  }, [token, isMobile]);

  const handleCopyLink = () => {
    if (stats?.share_link) {
      navigator.clipboard.writeText(stats.share_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareOnWhatsApp = () => {
    if (stats) {
      const text = `Rejoins-moi sur Kaneabourse ! Utilise mon code ${stats.referral_code} pour obtenir 15 jours de Pro gratuitement. C'est l'outil ultime pour investir à la BRVM 🚀 : ${stats.share_link}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  const shareOnLinkedIn = () => {
    if (stats) {
      const url = encodeURIComponent(stats.share_link);
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
    }
  };

  const shareOnEmail = () => {
    if (stats) {
      const subject = encodeURIComponent("Invitation : 15 jours offerts sur Kaneabourse Pro !");
      const body = encodeURIComponent(`Bonjour,\n\nJe t'invite à découvrir Kaneabourse, la plateforme d'analyse financière de référence pour la BRVM.\n\nEn t'inscrivant avec mon lien de parrainage, tu bénéficieras de 15 jours d'accès Pro gratuits (sans engagement) :\n${stats.share_link}\n\nMon code de parrainage : ${stats.referral_code}\n\nBons investissements !`);
      window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
    }
  };

  if (loading) {
    return (
      <SiteLayout>
        <Header icon="card_giftcard" title="Parrainage" />
        <div className="sponsorship-loading">
          <div className="spinner"></div>
          <p>Chargement de votre espace parrainage...</p>
        </div>
      </SiteLayout>
    );
  }

  if (error) {
    return (
      <SiteLayout>
        <Header icon="card_giftcard" title="Parrainage" />
        <div className="sponsorship-error" style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-muted)' }}>
          <i className="material-icons" style={{ fontSize: '48px', color: '#ef4444', marginBottom: '16px' }}>error_outline</i>
          <h2>Oups !</h2>
          <p>Impossible de charger vos statistiques de parrainage pour le moment.<br/>Veuillez réessayer plus tard.</p>
        </div>
      </SiteLayout>
    );
  }

  const commonProps = {
    stats,
    copied,
    onCopy: handleCopyLink,
    onWhatsApp: shareOnWhatsApp,
    onLinkedIn: shareOnLinkedIn,
    onEmail: shareOnEmail
  };

  console.log('[SponsorshipPage] Rendering final view layout. isMobile =', isMobile, 'has stats =', !!stats);

  return (
    <SiteLayout>
      <Header icon="card_giftcard" title="Parrainage" />
      <ErrorBoundary>
        {isMobile ? (
          <MobileSponsorship {...commonProps} />
        ) : (
          <DesktopSponsorship {...commonProps} />
        )}
      </ErrorBoundary>
    </SiteLayout>
  );
};

export default SponsorshipPage;
